import React from 'react';
import { Globe, Info, AlertTriangle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../lib/api';
import type { ReportFormState } from './OutreachReportWizard';

interface Props {
  itemId: string;
  formState: ReportFormState;
  onChange: (updates: Partial<ReportFormState>) => void;
}

const ReportStep1BasicInfo: React.FC<Props> = ({ itemId, formState, onChange }) => {
  // The public results link needs BOTH this report flagged public AND the outreach's own
  // "Share link to real-time results" setting on. Read the other half so we can say so.
  const { data: itemData } = useQuery(
    ['outreach-item-def', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}`);
      return res.data.data;
    },
    { staleTime: 5 * 60 * 1000, enabled: !!itemId }
  );

  const resultsLinkEnabled = itemData?.definition_json?.share_results_link === true;
  const showResultsLinkWarning = formState.isDefaultPublic && itemData && !resultsLinkEnabled;

  return (
    <div className="max-w-xl space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
          Report Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formState.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g., District 32 Report, Public Results, Youth Demographics"
          className="input w-full"
          autoFocus
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
          Description
        </label>
        <textarea
          value={formState.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="What is this report for? Who is the intended audience?"
          className="input w-full"
          rows={3}
        />
      </div>

      <div className="card">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formState.isDefaultPublic}
            onChange={(e) => onChange({ isDefaultPublic: e.target.checked })}
            className="mt-1 rounded border-border-medium"
          />
          <div>
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary-500" />
              <span className="font-medium text-text-primary dark:text-text-dark-primary">
                Set as Public Results View
              </span>
            </div>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
              This report will be shown to survey respondents when they click &quot;View Real-Time Results&quot; after completing the survey.
              Only one report can be the public view.
            </p>
          </div>
        </label>

        {showResultsLinkWarning && (
          <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20">
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-700 dark:text-amber-300">
              This outreach&apos;s <strong>Share link to real-time results</strong> setting is off, so
              respondents never see a &quot;View Real-Time Results&quot; link. Turn it on in the outreach&apos;s
              Settings step for this report to be reachable.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20">
        <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-blue-700 dark:text-blue-300">
          After naming your report, you&apos;ll configure filters (Step 2), build charts (Step 3), adjust settings (Step 4), and preview the final result (Step 5).
          The report is <strong>saved as soon as you continue</strong>, so you can close this and pick it up later from the Reports &amp; Communications tab.
        </p>
      </div>
    </div>
  );
};

export default ReportStep1BasicInfo;
